import React, { useEffect, useRef, useState } from 'react';
import { CATEGORY_GROUP_LABELS, type CategoryGroup } from '../types/prospecting';

interface Prospect {
  id: string;
  name: string;
  category: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
  website: string;
  lat: number;
  lng: number;
  openNow: boolean;
  reviews: number;
  score?: number;
  logo?: string;
  rating?: number;
  priceLevel?: number;
  photos?: string[];
  source?: 'google' | 'yelp' | 'osm' | 'demo';
}

interface GoogleMapProps {
  center: { lat: number; lng: number };
  prospects: Prospect[];
  radius: number;
  scanCoverageEntries?: Array<{
    id: string;
    lat: number;
    lng: number;
    radiusMiles: number;
    createdAt: string;
    categories?: string[];
    locationLabel?: string;
    prospectCount?: number;
  }>;
  coverageWindowDays?: number;
  onLocationChange?: (lat: number, lng: number) => void;
  selectedProspect?: Prospect | null;
  setSelectedProspect?: (prospect: Prospect | null) => void;
  isScanning?: boolean; // New prop to control radar animation
}

// Radar Animation Component
const RadarAnimation: React.FC<{ 
  isScanning: boolean; 
  radius: number; 
  center: { lat: number; lng: number };
  map: google.maps.Map | undefined;
}> = ({ isScanning, radius, center, map }) => {
  const [rotation, setRotation] = useState(0);
  const [blips, setBlips] = useState<Array<{ id: number; angle: number; distance: number; delay: number }>>([]);

  useEffect(() => {
    if (!isScanning) {
      setRotation(0);
      setBlips([]);
      return;
    }

    // Start radar rotation
    const rotationInterval = setInterval(() => {
      setRotation(prev => (prev + 2) % 360);
    }, 50); // Rotate 2 degrees every 50ms for smooth animation

    // Generate random blips
    const blipInterval = setInterval(() => {
      const newBlip = {
        id: Date.now(),
        angle: Math.random() * 360,
        distance: Math.random() * radius * 0.8, // Within 80% of radius
        delay: Math.random() * 1000 // Random delay for staggered appearance
      };
      setBlips(prev => [...prev, newBlip]);

      // Remove blip after 2 seconds
      setTimeout(() => {
        setBlips(prev => prev.filter(blip => blip.id !== newBlip.id));
      }, 2000);
    }, 300); // New blip every 300ms

    return () => {
      clearInterval(rotationInterval);
      clearInterval(blipInterval);
    };
  }, [isScanning, radius]);

  if (!isScanning) return null;

  return (
    <div style={{
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: `${radius * 2}px`,
      height: `${radius * 2}px`,
      pointerEvents: 'none',
      zIndex: 1000
    }}>
      {/* Radar circle */}
      <div style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: `${radius * 2}px`,
        height: `${radius * 2}px`,
        border: '2px solid rgba(59, 130, 246, 0.6)',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(59, 130, 246, 0.1) 0%, transparent 70%)'
      }} />

      {/* Radar sweep */}
      <div style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: `translate(-50%, -50%) rotate(${rotation}deg)`,
        width: `${radius * 2}px`,
        height: `${radius * 2}px`,
        background: 'conic-gradient(from 0deg, transparent 0deg, rgba(59, 130, 246, 0.4) 30deg, transparent 60deg)',
        borderRadius: '50%',
        transformOrigin: 'center'
      }} />

      {/* Radar line */}
      <div style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: `translate(-50%, -50%) rotate(${rotation}deg)`,
        width: '2px',
        height: `${radius}px`,
        background: 'linear-gradient(to bottom, rgba(59, 130, 246, 0.8), transparent)',
        transformOrigin: 'top center'
      }} />

      {/* Center dot */}
      <div style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '8px',
        height: '8px',
        background: '#3b82f6',
        borderRadius: '50%',
        boxShadow: '0 0 10px rgba(59, 130, 246, 0.8)'
      }} />

      {/* Blips */}
      {blips.map(blip => {
        const x = Math.cos((blip.angle * Math.PI) / 180) * blip.distance;
        const y = Math.sin((blip.angle * Math.PI) / 180) * blip.distance;
        
        return (
          <div
            key={blip.id}
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: `translate(${x}px, ${y}px)`,
              width: '6px',
              height: '6px',
              background: '#ef4444',
              borderRadius: '50%',
              boxShadow: '0 0 10px rgba(239, 68, 68, 0.9)',
              animation: 'blipPulse 2s ease-out',
              animationDelay: `${blip.delay}ms`
            }}
          />
        );
      })}

      {/* CSS Animation for blip pulse */}
      <style>{`
        @keyframes blipPulse {
          0% {
            opacity: 1;
            transform: scale(0.5);
          }
          50% {
            opacity: 0.8;
            transform: scale(1.2);
          }
          100% {
            opacity: 0;
            transform: scale(1.5);
          }
        }
      `}</style>
    </div>
  );
};

const MapComponent: React.FC<GoogleMapProps> = ({
  center,
  prospects,
  radius,
  scanCoverageEntries = [],
  coverageWindowDays = 90,
  onLocationChange,
  selectedProspect,
  setSelectedProspect,
  isScanning = false
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<google.maps.Map>();
  const [markers, setMarkers] = useState<google.maps.Marker[]>([]);
  const circleRef = useRef<google.maps.Circle | null>(null);
  const coverageCirclesRef = useRef<google.maps.Circle[]>([]);
  const coverageInfoWindowRef = useRef<google.maps.InfoWindow | null>(null);

  const formatCoverageCategories = (categories: string[] | undefined): string => {
    if (!Array.isArray(categories) || categories.length === 0) return 'All industries';
    return categories
      .map((category) => CATEGORY_GROUP_LABELS[category as CategoryGroup] || category.replace(/_/g, ' '))
      .join(', ');
  };

  useEffect(() => {
    if (ref.current && !map) {
      const newMap = new google.maps.Map(ref.current, {
        center,
        zoom: 12,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        zoomControl: true,
      });

      // Add click listener for manual location setting
      if (onLocationChange) {
        newMap.addListener('click', (e: google.maps.MapMouseEvent) => {
          if (e.latLng) {
            onLocationChange(e.latLng.lat(), e.latLng.lng());
          }
        });
      }

      setMap(newMap);
    }
  }, [ref, map, center, onLocationChange]);

  // Update map center when center prop changes
  useEffect(() => {
    if (map) {
      map.setCenter(center);
    }
  }, [map, center]);

  // Update markers when prospects change
  useEffect(() => {
    if (!map) return;

    // Clear existing markers
    markers.forEach(marker => marker.setMap(null));

    const newMarkers: google.maps.Marker[] = [];

    // Add user location marker
    const userMarker = new google.maps.Marker({
      position: center,
      map,
      title: 'Your Location',
      icon: {
        url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="8" fill="#3b82f6" stroke="#ffffff" stroke-width="2"/>
            <circle cx="12" cy="12" r="3" fill="#ffffff"/>
          </svg>
        `),
        scaledSize: new google.maps.Size(24, 24),
        anchor: new google.maps.Point(12, 12)
      }
    });
    newMarkers.push(userMarker);

    // Add prospect markers
    prospects.forEach((prospect, index) => {
      const score = prospect.score || 0;
      // Fix color logic to match legend: Green (>70%), Orange (40-70%), Red (<40%)
      const color = score > 0.7 ? '#10b981' : score > 0.4 ? '#f59e0b' : '#ef4444';

      const marker = new google.maps.Marker({
        position: { lat: prospect.lat, lng: prospect.lng },
        map,
        title: prospect.name,
        icon: {
          url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
            <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg"> 
              <circle cx="16" cy="16" r="14" fill="${color}" stroke="#ffffff" stroke-width="2"/>
              <text x="16" y="20" text-anchor="middle" fill="white" font-family="Arial, sans-serif" font-size="12" font-weight="bold">${index + 1}</text>
            </svg>
          `),
          scaledSize: new google.maps.Size(32, 32),
          anchor: new google.maps.Point(16, 16)
        }
      });

      // Add info window
      const infoWindow = new google.maps.InfoWindow({
        content: `
          <div style="font-family: Arial, sans-serif; max-width: 250px;">
            <h3 style="margin: 0 0 8px 0; color: #1f2937;">${prospect.name}</h3>
            <p style="margin: 0 0 4px 0; color: #6b7280; font-size: 14px;">${prospect.category}</p>
            <p style="margin: 0 0 8px 0; color: #374151; font-size: 13px;">${prospect.address}, ${prospect.city}, ${prospect.state} ${prospect.zip}</p>
            <div style="display: flex; gap: 8px; margin-top: 8px;">
              <span style="background: ${color}; color: white; padding: 2px 6px; border-radius: 4px; font-size: 12px; font-weight: bold;">
                ${(score * 100).toFixed(0)}%
              </span>
              ${prospect.rating ? `<span style="color: #f59e0b;">★ ${prospect.rating}</span>` : ''}
              ${prospect.openNow ? '<span style="color: #10b981; font-size: 12px;">Open Now</span>' : ''}
            </div>
          </div>
        `
      });

      marker.addListener('click', () => {
        if (setSelectedProspect) {
          setSelectedProspect(prospect);
        }
        infoWindow.open(map, marker);
      });

      newMarkers.push(marker);
    });

    setMarkers(newMarkers);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, prospects, center, setSelectedProspect]);

  // Update radius circle
  useEffect(() => {
    if (!map) return;

    // Clear existing circle
    if (circleRef.current) {
      circleRef.current.setMap(null);
    }

    // Add new circle
    const newCircle = new google.maps.Circle({
      strokeColor: '#3b82f6',
      strokeOpacity: 0.8,
      strokeWeight: 2,
      fillColor: '#3b82f6',
      fillOpacity: 0.1,
      map,
      center,
      radius: radius * 1609.34, // Convert miles to meters
    });

    circleRef.current = newCircle;

    // Cleanup function
    return () => {
      if (circleRef.current) {
        circleRef.current.setMap(null);
      }
    };
  }, [map, center, radius]);

  // Draw historical scan coverage marks (cooling over time).
  useEffect(() => {
    if (!map) return;

    coverageCirclesRef.current.forEach((c) => c.setMap(null));
    coverageCirclesRef.current = [];
    coverageInfoWindowRef.current?.close();

    if (!Array.isArray(scanCoverageEntries) || scanCoverageEntries.length === 0) return;

    const now = Date.now();
    const windowMs = Math.max(1, coverageWindowDays) * 24 * 60 * 60 * 1000;

    const circles: google.maps.Circle[] = [];
    scanCoverageEntries.forEach((entry) => {
      if (!Number.isFinite(entry?.lat) || !Number.isFinite(entry?.lng)) return;
      if (!Number.isFinite(entry?.radiusMiles) || entry.radiusMiles <= 0) return;
      const createdMs = new Date(entry.createdAt).getTime();
      if (!Number.isFinite(createdMs)) return;
      const ageRatio = Math.min(1, Math.max(0, (now - createdMs) / windowMs));
      const hue = Math.round(10 + ageRatio * 190); // hot orange -> cool blue
      const strokeOpacity = 0.75 - ageRatio * 0.45;
      const fillOpacity = 0.22 - ageRatio * 0.15;

      const c = new google.maps.Circle({
        strokeColor: `hsl(${hue}, 85%, 42%)`,
        strokeOpacity: Math.max(0.2, strokeOpacity),
        strokeWeight: ageRatio < 0.25 ? 2 : 1,
        fillColor: `hsl(${hue}, 85%, 50%)`,
        fillOpacity: Math.max(0.05, fillOpacity),
        map,
        center: { lat: entry.lat, lng: entry.lng },
        radius: entry.radiusMiles * 1609.34
      });
      c.addListener('click', (event: google.maps.MapMouseEvent) => {
        const categoriesText = formatCoverageCategories(entry.categories);
        const infoWindow =
          coverageInfoWindowRef.current ||
          new google.maps.InfoWindow();
        coverageInfoWindowRef.current = infoWindow;
        infoWindow.setContent(`
          <div style="font-family: Arial, sans-serif; max-width: 260px;">
            <div style="font-weight: 700; color: #111827; margin-bottom: 6px;">${entry.locationLabel || 'Scan area'}</div>
            <div style="font-size: 12px; color: #4b5563; margin-bottom: 4px;">${new Date(entry.createdAt).toLocaleString()}</div>
            <div style="font-size: 12px; color: #111827; margin-bottom: 4px;">Radius: ${entry.radiusMiles.toFixed(1)} miles</div>
            <div style="font-size: 12px; color: #111827; margin-bottom: 4px;">Industries: ${categoriesText}</div>
            ${typeof entry.prospectCount === 'number' ? `<div style="font-size: 12px; color: #111827;">Prospects: ${entry.prospectCount}</div>` : ''}
          </div>
        `);
        infoWindow.setPosition(event.latLng || { lat: entry.lat, lng: entry.lng });
        infoWindow.open(map);
      });
      circles.push(c);
    });

    coverageCirclesRef.current = circles;
    return () => {
      circles.forEach((c) => c.setMap(null));
    };
  }, [map, scanCoverageEntries, coverageWindowDays]);

  return (
    <div style={{ position: 'relative', width: '100%', height: '500px' }}>
      <div ref={ref} style={{ width: '100%', height: '100%' }} />
      <RadarAnimation 
        isScanning={isScanning} 
        radius={radius * 50} 
        center={center}
        map={map}
      /> {/* Scale radius for visual effect */}
    </div>
  );
};

const GoogleMap: React.FC<GoogleMapProps> = (props) => {
  const apiKey = process.env.REACT_APP_GOOGLE_PLACES_API_KEY || '';
  const [apiLoaded, setApiLoaded] = useState(false);

  // Dynamically load Google Maps API with a minimal duplicate guard (StrictMode safe)
  useEffect(() => {
    if (!apiKey) return;

    // If already loaded, mark ready
    if (window.google?.maps) {
      setApiLoaded(true);
      return;
    }

    // Reuse existing script tag if present to prevent duplicate loads in StrictMode
    const existing = document.querySelector('script[data-raedar="gmaps"]') as HTMLScriptElement | null
      || document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]') as HTMLScriptElement | null;

    if (existing) {
      existing.addEventListener('load', () => setApiLoaded(true), { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.setAttribute('data-raedar', 'gmaps');
    script.onload = () => {
      console.log('✅ Google Maps API loaded successfully');
      setApiLoaded(true);
    };
    script.onerror = () => {
      console.error('❌ Failed to load Google Maps API');
    };
    document.head.appendChild(script);
  }, [apiKey]);

  if (!apiKey) {
    return (
      <div style={{
        width: '100%',
        height: '500px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#fef3c7',
        border: '1px solid #fcd34d',
        borderRadius: '8px',
        color: '#92400e',
        textAlign: 'center',
        padding: '20px'
      }}>
        <div>
          <h3 style={{ margin: '0 0 8px 0' }}>Google Maps API Key Required</h3>
          <p style={{ margin: 0, fontSize: '14px' }}>
            Please add your Google Maps API key to the .env file as REACT_APP_GOOGLE_PLACES_API_KEY
          </p>
        </div>
      </div>
    );
  }

  if (!apiLoaded || !window.google?.maps) {
    return (
      <div style={{
        width: '100%',
        height: '500px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f3f4f6',
        border: '1px solid #d1d5db',
        borderRadius: '8px'
      }}>
        Loading Google Maps...
      </div>
    );
  }

  return <MapComponent {...props} />;
};

export default GoogleMap;